import { DatePicker, Form, Input } from "antd";
import { DrawerCollapsible } from "components/UI/DrawerCollapsible/DrawerCollapsible";
import { DrawerContent } from "components/UI/DrawerContent/DrawerContent";
import { GeneralFormProvider } from "components/UI/GeneralForm/GeneralFormProvider";
import { useGeneralForm } from "components/UI/GeneralForm/hooks/useGeneralForm";
import dayjs from "dayjs";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { DATE_FORMAT } from "localConstants";
import { FC } from "react";
import { FormItem } from "react-hook-form-antd";
import { FormType } from "types/models";

import { schema, TransactionReportFormData } from "./formSchema";

import PayoutData = App.Data.PayoutData;

interface ContentProps {
    payout: PayoutData;
    formType: FormType;
}

const ManageTransactionFormContent: FC<ContentProps> = ({ payout, formType }) => {
    const { t: tF } = usePaldockTranslation("forms.manage-transaction");
    const { t: tFields } = usePaldockTranslation("forms.general-fields");
    const { t: tG } = usePaldockTranslation("tables.general-columns");
    const { control, fieldNames } = useGeneralForm<TransactionReportFormData>();

    const disableFields = formType === FormType.Detail;

    return (
        <DrawerContent>
            <Form layout="vertical">
                <DrawerCollapsible title={tF("sections.transactions")}>
                    <div className="grid rounded-lg grid-cols-3 gap-x-4 col-span-3">
                        <Form.Item name="firstName" label={tFields("country.label")}>
                            <Input disabled={disableFields} />
                        </Form.Item>
                        <Form.Item name="category" label={tFields("category.label")}>
                            <Input disabled={disableFields} />
                        </Form.Item>
                        <Form.Item name="username" label={tFields("offer.label")}>
                            <Input disabled={disableFields} />
                        </Form.Item>

                        <FormItem
                            control={control}
                            name={fieldNames.created_at.name}
                            label={tFields("created_at.label")}
                        >
                            <DatePicker
                                format={DATE_FORMAT}
                                value={dayjs(payout.created_at)}
                                readOnly
                                className="w-full"
                            />
                        </FormItem>
                        <FormItem
                            control={control}
                            name={fieldNames.updated_at.name}
                            label={tFields("updated_at.label")}
                        >
                            <DatePicker
                                className="w-full"
                                format={DATE_FORMAT}
                                value={dayjs(payout.updated_at)}
                                readOnly
                            />
                        </FormItem>

                        <Form.Item name="type1" label={tFields("type.label")}>
                            <Input disabled={disableFields} />
                        </Form.Item>
                        <Form.Item name="user" label={tF("fields.user.label")}>
                            <Input disabled={disableFields} />
                        </Form.Item>

                        <FormItem control={control} name={fieldNames.result.name} label={tFields("status.label")}>
                            <Input disabled />
                        </FormItem>

                        <FormItem
                            control={control}
                            name={fieldNames.formatted_amount?.name}
                            label={tF("fields.formatted_amount.label")}
                        >
                            <Input readOnly />
                        </FormItem>

                        <Form.Item name="transaction_id" label={tF("fields.transaction_id.label")}>
                            <Input disabled={disableFields} />
                        </Form.Item>
                        <Form.Item name="commission_id" label={tG("commission_id")}>
                            <Input disabled={disableFields} />
                        </Form.Item>
                        <Form.Item name="external_id" label={tF("fields.external_id.label")}>
                            <Input disabled={disableFields} />
                        </Form.Item>
                        <Form.Item name="conversion_id" label={tF("fields.conversion_id.label")}>
                            <Input disabled={disableFields} />
                        </Form.Item>
                    </div>
                </DrawerCollapsible>
            </Form>
        </DrawerContent>
    );
};

interface ManageTransactionFormProps {
    payout: PayoutData;
    onUpdate?: () => void;
    handleClose?: () => void;
    formType: FormType;
}

export const ManageTransactionForm: FC<ManageTransactionFormProps> = ({ payout, formType }) => {
    const defaultValues: TransactionReportFormData = {
        ...payout,
        created_at: dayjs(payout.created_at),
        updated_at: dayjs(payout.updated_at),
        formatted_amount: payout.formatted_amount || "",
        result: payout.result || "",
    };

    return (
        <GeneralFormProvider<TransactionReportFormData> schema={schema} defaultValues={defaultValues}>
            <ManageTransactionFormContent payout={payout} formType={formType} />
        </GeneralFormProvider>
    );
};
